import { apiErrorMessage } from '@agos/api-client'
import {
  getGetEmployeesMyDeliveriesQueryKey,
  getGetEmployeesMyWalletQueryKey,
  useGetEmployeesMyDeliveries,
  useGetEmployeesMyWallet,
  usePatchEmployeesMyAvailability,
  usePatchEmployeesMyVehicle,
} from '@agos/api-client/hr'
import { useGetVehicles, usePostOrdersOrderIdConfirmStatus } from '@agos/api-client/orders'
import { useGetSettingsQrPaymentActive } from '@agos/api-client/payments'
import {
  T,
  formatCents,
  formatCount,
  paymentMethodBadge,
  surfacesFor,
  tt,
  useAuth,
} from '@agos/shared'
import { ConfirmDialog, Spinner, cn, useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  Clock,
  MapPin,
  Navigation,
  Phone,
  LayoutDashboard,
  LogOut,
  Power,
  QrCode,
  RefreshCw,
  Truck,
  Undo2,
  User,
  Wallet,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'wouter'

import { TransferPaymentSheet } from '../qr/TransferPaymentSheet'
import { offersQr } from '../qr/qr'
import {
  buzz,
  currentVehicle,
  collectCents,
  collectLabel,
  deliveriesOf,
  elapsedLabel,
  emptyBoardNote,
  minutesWaiting,
  navigationUrl,
  orderedDeliveries,
  runTotals,
  shiftLabel,
  statusLabel,
  telUrl,
  handBackNote,
  handBackReason,
  mayHandBack,
  urgencyOf,
  walletNote,
  vehicleLabel,
  vehicleNote,
  vehiclesOf,
  walletOf,
  type Delivery,
} from './driving'

/**
 * `/driving` — §9.6's driving surface, and the destination §9.1's driver tile routes to.
 *
 * Fullscreen and phone-first: no sidebar, no header, one column at every width, every action a full-width
 * control a thumb can hit. A driver holds the phone in one hand.
 *
 * **FB-180** is why none of `driver.tsx`'s wiring is here — see `driving.ts` and the module spec. In short:
 * it filtered drivers on a column that does not exist, matched orders on the wrong one, and had no
 * authentication at all. The signed-in employee is the driver; the server scopes every request to them.
 *
 * The 15-second poll is legacy's interval, copied exactly (§14.1).
 */

const POLL_MS = 15_000

/**
 * Whether the three run figures are drawn above the list - §9.384, FB-683.
 *
 * **False on the owner's instruction, and a constant rather than a deletion:** *"hide it for now, do
 * not fully remove it… maybe we will give it back later."* The block below is still compiled and
 * still type-checked against `totals`, so it cannot drift out of date while it is off, and turning
 * it back on is this one word.
 */
const SHOW_RUN_TOTALS = false

export function DrivingPage() {
  const { toast } = useToast()
  // §9.162 — who this driver is decides whether *Aplikácia* is offered, and `logout` is the way out
  // of a surface that draws no sidebar.
  const { user, logout } = useAuth()
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<Delivery | null>(null)
  /**
   * §9.370 - the order the customer would not take.
   *
   * Separate from `confirming` because they are different acts with different consequences, and one
   * dialog for both is how somebody confirms the wrong one.
   */
  const [handingBack, setHandingBack] = useState<Delivery | null>(null)
  const [paying, setPaying] = useState<Delivery | null>(null)
  // Stamped per render pass rather than per row, so a list cannot disagree with itself while it is read.
  const [openedAt] = useState(() => Date.now())

  const deliveryQuery = useGetEmployeesMyDeliveries({ query: { refetchInterval: POLL_MS } })
  const walletQuery = useGetEmployeesMyWallet()
  const vehicleQuery = useGetVehicles()
  // Public by design — "the driver's app asks before it knows who is signed in, and the answer names
  // nothing but a bank".
  const qrActiveQuery = useGetSettingsQrPaymentActive()
  // The cast this carried is gone: `/settings/qr-payment/active` is typed now (BLOCKED #4), so the
  // generated client answers `QrPaymentAvailability` and the shape is the backend's to state.
  const qrActive = qrActiveQuery.data?.isActive === true

  const deliveries = useMemo(
    () => orderedDeliveries(deliveriesOf(deliveryQuery.data)),
    [deliveryQuery.data],
  )
  const wallet = useMemo(() => walletOf(walletQuery.data), [walletQuery.data])
  const vehicles = useMemo(() => vehiclesOf(vehicleQuery.data), [vehicleQuery.data])
  const vehicle = currentVehicle(wallet, vehicles)
  const totals = runTotals(deliveries)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesMyDeliveriesQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesMyWalletQueryKey() })
  }

  const setVehicle = usePatchEmployeesMyVehicle({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({ title: tt('Vozidlo prepnuté') })
      },
      // §9.385 - a vehicle somebody else is on, or one that was retired, is named by the server.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Vozidlo sa nepodarilo prepnúť'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const setShift = usePatchEmployeesMyAvailability({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({ title: tt('Smena prepnutá') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Smenu sa nepodarilo prepnúť'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const deliver = usePostOrdersOrderIdConfirmStatus({
    mutation: {
      onSuccess: () => {
        setConfirming(null)
        setHandingBack(null)
        refresh()
        toast({ title: tt('Doručené') })
      },
      // §9.385 - **the one a driver reads at a door.** `confirm-status` refuses a handover by name
      // (an order already closed, one not assigned to this driver), and *"skúste to znova"* is advice
      // that cannot be right for every one of those.
      onError: (caught) => {
        setConfirming(null)
        setHandingBack(null)
        toast({
          variant: 'destructive',
          title: tt('Stav sa nezmenil'),
          description: apiErrorMessage(
            caught,
            tt('Skúste to znova, alebo zavolajte dispečerovi.'),
          ),
        })
      },
    },
  })

  return (
    <div className="min-h-screen bg-background pb-10" data-driving>
      {/* ─── who and what, pinned to the top ─── */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold leading-tight truncate">{wallet?.name ?? '-'}</p>
            <p className="text-xs text-muted-foreground truncate">
              {wallet === null ? '' : shiftLabel(wallet)}
              {wallet?.vehicleName != null && ` · ${wallet.vehicleName}`}
            </p>
          </div>
          <button
            type="button"
            data-refresh
            onClick={refresh}
            aria-label={tt('Obnoviť')}
            className="p-3 rounded-xl hover:bg-muted"
          >
            <RefreshCw className="w-5 h-5 text-muted-foreground" />
          </button>
          {/* §9.162 - the way back and the way out, neither of which this header had.
            *
            * The manager's sign-out lives in the sidebar footer (§9.7.3) and this page is
            * full-screen, so a driver who signed in here could not sign out — on a phone handed
            * between shifts, that is somebody else's session left open. `surfacesFor` decides
            * whether *Aplikácia* is offered at all: it is the same permission answer the header
            * shortcut asks, not a second rule. */}
          {surfacesFor(user?.systemRole).some((option) => option.surface === 'app') && (
            <a
              href="/dashboard"
              data-surface-link="app"
              aria-label={tt('Aplikácia')}
              title={tt('Aplikácia')}
              className="p-3 rounded-xl hover:bg-muted"
            >
              <LayoutDashboard className="w-5 h-5 text-muted-foreground" />
            </a>
          )}
          <button
            type="button"
            data-driving-logout
            onClick={() => void logout()}
            aria-label={tt('Odhlásiť sa')}
            title={tt('Odhlásiť sa')}
            className="p-3 rounded-xl hover:bg-muted"
          >
            <LogOut className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* The run, from the deliveries themselves - never from the lifetime wallet.
          *
          * §9.384, FB-683 - **hidden, not deleted.** The owner: *"hide it for now, do not fully
          * remove it… drivers are reporting that its useless info (maybe it is, but maybe we will
          * give it back later)"*. The constant keeps the block compiled and type-checked against
          * `totals`, so turning it back on is one word; deleting it would mean writing it again
          * from memory. */}
        {SHOW_RUN_TOTALS && (
        <div className="grid grid-cols-3 divide-x divide-border border-t border-border" data-run-totals>
          <Cell label={tt('Zastávok')} value={formatCount(totals.stops)} />
          <Cell
            label={tt('Vyberiete')}
            value={formatCents(totals.collectCents)}
            tone="text-emerald-500"
          />
          <Cell label={tt('Zaplatené vopred')} value={formatCount(totals.alreadyPaid)} />
        </div>
        )}
      </header>

      <div className="px-4 pt-4 space-y-4 max-w-lg mx-auto">
        {/* The shift switch is the only thing a driver can change about themselves here. */}
        {wallet !== null && (
          <button
            type="button"
            data-shift={wallet.isAvailable ? 'on' : 'off'}
            disabled={setShift.isPending}
            onClick={() => {
              buzz(navigator)
              setShift.mutate({ data: { isAvailable: !wallet.isAvailable } })
            }}
            className={cn(
              'w-full min-h-[48px] rounded-2xl font-bold flex items-center justify-center gap-2 border-2 transition-colors disabled:opacity-50',
              wallet.isAvailable
                ? 'border-emerald-500/40 text-emerald-500 bg-emerald-500/5'
                : 'border-border text-muted-foreground',
            )}
          >
            <Power className="w-5 h-5" />
            {wallet.isAvailable ? tt('Na smene - klepnutím ukončíte') : tt('Mimo smeny - klepnutím začnete')}
          </button>
        )}

        {/* §9.6: which van. `/trip-log` and `/delivery-statistics` attribute distance and fuel by vehicle,
          * so a driver left on last week's van puts this week's diesel on it. */}
        {vehicles.length > 0 && (
          <div className="space-y-1.5" data-vehicle={vehicle?.id ?? 'none'}>
            <select
              value={vehicle === null ? '' : String(vehicle.id)}
              onChange={(event) =>
                setVehicle.mutate({
                  data: { vehicleId: event.target.value === '' ? null : Number(event.target.value) },
                })
              }
              aria-label={tt('Vozidlo')}
              data-vehicle-select
              disabled={setVehicle.isPending}
              className="w-full min-h-[48px] rounded-2xl bg-card border border-border px-3 text-sm font-semibold outline-none disabled:opacity-50"
            >
              <option value="">{tt('Bez vozidla')}</option>
              {vehicles.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {vehicleLabel(entry)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-white/50" data-vehicle-note>
              {vehicleNote(vehicle)}
            </p>
          </div>
        )}

        {deliveryQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-16 space-y-3" data-empty>
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto">
              <CircleCheck className="w-9 h-9 text-muted-foreground" />
            </div>
            <p className="font-semibold">
              <T>Žiadne aktívne rozvozy</T>
            </p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">{emptyBoardNote(wallet)}</p>
          </div>
        ) : (
          deliveries.map((delivery) => (
            <DeliveryCard
              onQr={offersQr(delivery.paymentMethod, qrActive) ? () => setPaying(delivery) : null}
              key={delivery.id}
              delivery={delivery}
              minutes={minutesWaiting(delivery, openedAt)}
              busy={deliver.isPending}
              onDeliver={() => {
                buzz(navigator)
                setConfirming(delivery)
              }}
              onHandBack={
                // Only from the field - the same condition the server enforces, so the button is
                // absent rather than refused.
                mayHandBack(delivery.status)
                  ? () => {
                      buzz(navigator)
                      setHandingBack(delivery)
                    }
                  : null
              }
            />
          ))
        )}

        {/* The lifetime figures, labelled as lifetime - and the way to the day that is actually being
          * asked about at handover. */}
        {wallet !== null && (
          <p className="text-[11px] text-muted-foreground pt-2" data-wallet-note>
            {walletNote(wallet)}
          </p>
        )}
        <Link
          href="/wallet"
          data-wallet-link
          className="flex items-center justify-between gap-2 min-h-[48px] rounded-2xl bg-card border border-border px-4 text-sm font-semibold hover:bg-muted"
        >
          <span className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            <T>Peňaženka - čo odovzdávam</T>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      </div>

      {paying !== null && (
        /* §9.338 - the bank answers now, so there is no *Zaplatil / Nezaplatil* pair to press.
           The sheet opens a real TatraPay session and shows the server's countdown. */
        <TransferPaymentSheet
          orderId={paying.id}
          orderNumber={paying.orderNumber}
          onClose={() => setPaying(null)}
          onPaid={refresh}
        />
      )}

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={tt('Označiť ako doručenú?')}
        description={
          confirming === null
            ? ''
            : `#${confirming.orderNumber} · ${collectLabel(confirming)}. ${tt('Toto sa nedá vrátiť - stav uvidí kuchyňa aj dispečer.')}`
        }
        confirmLabel={tt('Doručené')}
        loading={deliver.isPending}
        onConfirm={() => {
          const delivery = confirming
          if (delivery === null) return
          deliver.mutate({
            orderId: delivery.id,
            data: { newStatus: 'delivered', reason: tt('Doručené šoférom') },
          })
        }}
      />

      {/* §9.370, FB-658 - the customer refused it. `quality_check` rather than `cancelled`: the food
        * exists and somebody has to decide what happens to it, and cancelling is a money decision an
        * admin makes, not a driver at the door. */}
      <ConfirmDialog
        open={handingBack !== null}
        onClose={() => setHandingBack(null)}
        title={tt('Vrátiť objednávku späť?')}
        description={handingBack === null ? '' : handBackNote(handingBack.orderNumber)}
        confirmLabel={tt('Vrátiť späť')}
        loading={deliver.isPending}
        onConfirm={() => {
          const delivery = handingBack
          if (delivery === null) return
          deliver.mutate({
            orderId: delivery.id,
            // The server permits this for a driver only from the field, which is why the button is
            // not offered anywhere else.
            data: { newStatus: 'quality_check', reason: handBackReason() },
          })
        }}
      />
    </div>
  )
}

function Cell({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="px-3 py-2.5 text-center">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={cn('font-bold text-lg leading-tight tabular-nums', tone)}>{value}</p>
    </div>
  )
}

function DeliveryCard({
  delivery,
  minutes,
  busy,
  onDeliver,
  onHandBack,
  onQr,
}: {
  delivery: Delivery
  minutes: number
  busy: boolean
  onDeliver: () => void
  /**
   * §9.370 - the customer refused the food at the door.
   *
   * Offered only while the order is out in the field, because that is the only state the server
   * lets a driver hand one back from (`IN_THE_FIELD`).
   */
  onHandBack: (() => void) | null
  /** Null where there is nothing left to transfer — an order paid online or on a terminal. */
  onQr: (() => void) | null
}) {
  const urgency = urgencyOf(minutes)
  const maps = navigationUrl(delivery.customerAddress)
  const phone = telUrl(delivery.customerPhone)
  /**
   * §9.384, FB-683 - *"make sure, that whole objednávka can be collapsed"*.
   *
   * **Collapsed by default since §9.387**, on the owner's instruction: *"by default make all
   * objednávky collapsed in driver app"*. §9.384 chose expanded and argued for it - *"a driver who
   * has pressed nothing sees the card they saw yesterday"* - which is a good argument about not
   * surprising people and the wrong one about what a driver needs: a list of doors. Four stops on a
   * phone means scrolling past stop one's items to reach stop two.
   * `agos-a-recorded-decision-can-still-be-wrong`.
   *
   * Still **per card** and still not remembered between visits: a collapse is an answer about *this*
   * door, and a setting that survived a reload would hide an address somebody forgot they hid.
   */
  const [collapsed, setCollapsed] = useState(true)
  const method = paymentMethodBadge(delivery.paymentMethod)

  return (
    <article
      className={cn(
        'bg-card border-2 rounded-2xl overflow-hidden',
        urgency === 'late'
          ? 'border-red-500/50'
          : urgency === 'watch'
            ? 'border-amber-500/50'
            : 'border-violet-500/40',
      )}
      data-delivery={delivery.orderNumber}
      data-urgency={urgency}
    >
      <div className="px-4 pt-4 pb-3 space-y-2.5">
        {/* §9.384, FB-683 - **the two things a driver reads at a door, first and large.** The owner:
          * *"číslo objednávky, typ platby (Hotovosť, Karta) make sure, those are bigger, important
          * things for driver."* The number was `text-xs`, the smallest type on the screen, and the
          * method was an unstyled line under the total. */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-collapse={delivery.orderNumber}
            aria-expanded={!collapsed}
            aria-label={collapsed ? tt('Rozbaliť') : tt('Zbaliť')}
            onClick={() => setCollapsed((shown) => !shown)}
            className="p-1 -ml-1 rounded-lg text-muted-foreground hover:bg-muted shrink-0"
          >
            {collapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
          <span className="font-mono text-2xl font-black text-primary leading-none">
            #{delivery.orderNumber}
          </span>
          {/* The board's own vocabulary and tones - *"match colors from Rozvoz nástenka"*. */}
          {method !== null && (
            <span
              data-payment-method={delivery.paymentMethod}
              className={cn(
                'text-sm font-bold px-2.5 py-1 rounded-lg border ml-auto',
                method.tone,
              )}
            >
              {method.label}
            </span>
          )}
        </div>

        {/* Collapsed, this is the whole card above the buttons - §9.387, and the owner named what
          * belongs here: *"we have price under objednávka, thats cool, but remove «Vyberte 15,50 e»
          * and add adresa there"*. The collect badge said the total a second time; the address is
          * the one thing that says where to go. */}
        {collapsed && (
          <div className="space-y-1" data-collapsed-summary>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-bold text-2xl tabular-nums">
                {formatCents(delivery.totalCents)}
              </span>
              {/* The one thing the total cannot say: that there is nothing to take at this door.
                * Short, because it is beside an address on a phone. */}
              {collectCents(delivery) === null && (
                <span
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-blue-500/15 text-blue-500 shrink-0"
                  data-collapsed-paid
                >
                  <T>zaplatené</T>
                </span>
              )}
            </div>
            {delivery.customerAddress !== null && (
              <p className="flex items-start gap-1.5 text-sm font-medium" data-collapsed-address>
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                {delivery.customerAddress}
              </p>
            )}
          </div>
        )}

        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] px-2 py-0.5 rounded-lg bg-muted text-muted-foreground">
              {statusLabel(delivery.status)}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto" data-elapsed>
              <Clock className="w-3 h-3" />
              {elapsedLabel(minutes)}
            </span>
          </div>
        )}

        {!collapsed && delivery.customerName !== null && (
          <p className="font-bold flex items-center gap-2 text-base">
            <User className="w-4 h-4 text-muted-foreground shrink-0" /> {delivery.customerName}
          </p>
        )}

        {!collapsed && delivery.customerAddress !== null && (
          <p className="flex items-start gap-2 text-sm font-medium">
            <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            {delivery.customerAddress}
          </p>
        )}

        {!collapsed && delivery.notes !== null && (
          <p className="text-xs text-amber-500 flex items-start gap-1.5" data-note>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            {delivery.notes}
          </p>
        )}

        {!collapsed && delivery.items.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {delivery.items
              .map((item) => `${formatCount(item.quantity)}× ${item.productName}`)
              .join(' · ')}
          </p>
        )}

        {/* What to take at the door, said plainly - a driver should not be working it out from a
          * total. Expanded only: collapsed draws the same two above. */}
        {!collapsed && (
        <div className="flex items-center justify-between pt-1">
          <span className="font-bold text-lg tabular-nums">{formatCents(delivery.totalCents)}</span>
          <span
            className={cn(
              'text-xs font-semibold px-2 py-1 rounded-lg',
              collectLabel(delivery).startsWith(tt('Vyberte'))
                ? 'bg-emerald-500/15 text-emerald-600'
                : 'bg-blue-500/15 text-blue-500',
            )}
            data-collect
          >
            {collectLabel(delivery)}
          </span>
        </div>
        )}
      </div>

      {/* Two taps a driver makes with a bag in the other hand: navigate, and call. */}
      <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
        <a
          href={maps ?? undefined}
          target="_blank"
          rel="noreferrer"
          data-navigate={maps === null ? 'none' : 'maps'}
          aria-disabled={maps === null}
          className={cn(
            'min-h-[48px] flex items-center justify-center gap-2 text-sm font-bold',
            maps === null ? 'text-muted-foreground/40 pointer-events-none' : 'text-primary',
          )}
        >
          <Navigation className="w-4 h-4" /> <T>Navigovať</T>
        </a>
        <a
          href={phone ?? undefined}
          // §9.381, FB-676 - **the same two attributes the navigate link beside it has had all
          // along.** The owner: *"when driver is there, he will use his phone to call, so he will
          // not get odhlásiť… so he can still work in the driver app."* A `tel:` href with no
          // target navigates **this** document: on a phone with the app on the home screen the
          // document is unloaded for the hand-off, and the driver comes back to a cold boot - which
          // is what being logged out looks like from the pavement. The asymmetry between these two
          // links, in one `<div>`, is what says it was an oversight rather than a decision.
          target="_blank"
          rel="noreferrer"
          data-call={phone === null ? 'none' : 'tel'}
          aria-disabled={phone === null}
          className={cn(
            'min-h-[48px] flex items-center justify-center gap-2 text-sm font-bold',
            phone === null ? 'text-muted-foreground/40 pointer-events-none' : 'text-blue-500',
          )}
        >
          <Phone className="w-4 h-4" /> <T>Zavolať</T>
        </a>
      </div>

      {/* §9.384 - not in the owner's collapsed list, and both are deliberate actions rather than the
        * three a driver repeats at every door. */}
      {!collapsed && onQr !== null && (
        <button
          type="button"
          data-qr-open={delivery.orderNumber}
          onClick={onQr}
          className="w-full min-h-[48px] border-t border-border text-sm font-bold flex items-center justify-center gap-2 text-primary hover:bg-muted transition-colors"
        >
          <QrCode className="w-4 h-4" /> <T>Zaplatiť prevodom</T>
        </button>
      )}

      <button
        type="button"
        data-deliver={delivery.orderNumber}
        disabled={busy}
        onClick={onDeliver}
        className="w-full min-h-[52px] bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
      >
        <CircleCheck className="w-5 h-5" /> <T>Označiť ako doručenú</T>
      </button>

      {/* §9.370, FB-658 - the flow nobody had: the customer will not take it. The server has allowed
        * a driver to hand an order back since `IN_THE_FIELD` was written; nothing could reach it. */}
      {!collapsed && onHandBack !== null && (
        <button
          type="button"
          data-hand-back={delivery.orderNumber}
          disabled={busy}
          onClick={onHandBack}
          className="w-full min-h-[48px] border-t border-border text-sm font-bold flex items-center justify-center gap-2 text-amber-600 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
        >
          <Undo2 className="w-4 h-4" /> <T>Vrátiť späť</T>
        </button>
      )}
    </article>
  )
}
